import { supabase } from './supabase/client';
import type {
  AiInsightsResult,
  GenerateInsightsError,
  GenerateInsightsErrorCode,
} from '@flows.cash/types';

// Sonnet over a quarter of aggregates: uncached generations can take 15-30s.
const CLIENT_TIMEOUT_MS = 60_000;

export type GenerateInsightsResponse =
  | { data: AiInsightsResult; error: null }
  | { data: null; error: GenerateInsightsError };

function errorResponse(code: GenerateInsightsErrorCode): GenerateInsightsResponse {
  return { data: null, error: { error: code } };
}

/**
 * Fetches AI-written spending insights from the generate-insights Edge
 * Function. Results are cached server-side per (user, month, locale), so
 * repeat calls within a day are fast and free; pass forceRegenerate to
 * refresh. Never throws — all failures resolve to a GenerateInsightsError.
 */
export async function generateInsights(options?: {
  locale?: string;
  forceRegenerate?: boolean;
}): Promise<GenerateInsightsResponse> {
  const timeout = new Promise<GenerateInsightsResponse>((resolve) =>
    setTimeout(() => resolve(errorResponse('insights_unavailable')), CLIENT_TIMEOUT_MS)
  );

  const request = (async (): Promise<GenerateInsightsResponse> => {
    try {
      const { data, error } = await supabase.functions.invoke('generate-insights', {
        body: {
          locale: options?.locale ?? 'en',
          forceRegenerate: options?.forceRegenerate ?? false,
        },
      });

      if (error) {
        const status: number | undefined = (error as any)?.context?.status;
        if (status === 429) return errorResponse('rate_limited');
        if (status === 422) return errorResponse('no_data');
        return errorResponse('insights_unavailable');
      }

      if (typeof data?.summary !== 'string' || !Array.isArray(data?.items)) {
        return errorResponse('insights_unavailable');
      }

      return { data: data as AiInsightsResult, error: null };
    } catch (err) {
      console.error('generateInsights failed:', err);
      return errorResponse('insights_unavailable');
    }
  })();

  return Promise.race([request, timeout]);
}
